import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, Search, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { searchEventSessions } from "@/modules/eventRegistration/features/EventWaitlist/api/searchEventSessions/searchEventSessionsApi";
import { SessionSearchResult } from "@/modules/eventRegistration/features/EventWaitlist/api/searchEventSessions/types";
import { SessionRow, SectionHeader, Divider } from "./SessionRow";

interface SessionSwitcherProps {
  /** Event UUID for the session currently being viewed. */
  currentEventUuid: string;
  /** Event name for the session currently being viewed. */
  currentEventName: string;
  /** Session name (often equals eventName) for the session currently being viewed. */
  currentSessionName: string | null;
  /** Session UUID currently being viewed. */
  currentSessionUuid: string;
  /** Pre-formatted date · tz, e.g. "Apr 15, 2026 · CT". */
  currentSessionDateLabel: string;
  /** Optional venue label for the current session. */
  currentVenueName: string | null;
  /**
   * Trailing path segment to preserve when switching sessions —
   * e.g. "waitlist". The new URL is
   * `/admin/events/{eventUuid}/sessions/{sessionUuid}/{trailingPath}`.
   */
  trailingPath: string;
}

const PAGE_SIZE = 25;

/**
 * Header trigger + popover for jumping between any session in the
 * platform. The trigger is the page title itself (Event › Session) so
 * the switcher is co-located with the identity it changes.
 *
 * Empty query  → upcoming sessions (BE filters by endDate >= now).
 * With query   → ILIKE match across event/session names.
 *
 * Display rule for the secondary line: drop sessionName when it
 * duplicates the event name (the common case — most sessions inherit
 * their parent event's name). Show it only when it actually
 * distinguishes the session (e.g. "Cohort 3").
 */
export function SessionSwitcher({
  currentEventUuid,
  currentEventName,
  currentSessionName,
  currentSessionUuid,
  currentSessionDateLabel,
  currentVenueName,
  trailingPath,
}: SessionSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 200);
  const [results, setResults] = useState<SessionSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOffset(0);
  }, [debouncedQuery, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    searchEventSessions({ q: debouncedQuery, limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        setResults(res.data ?? []);
        setTotalCount(res.meta?.totalCount ?? res.data?.length ?? 0);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open]);

  const loadMore = () => {
    const nextOffset = offset + PAGE_SIZE;
    setIsLoadingMore(true);
    searchEventSessions({
      q: debouncedQuery,
      limit: PAGE_SIZE,
      offset: nextOffset,
    })
      .then((res) => {
        setResults((prev) => [...prev, ...(res.data ?? [])]);
        setOffset(nextOffset);
      })
      .catch(() => {})
      .finally(() => setIsLoadingMore(false));
  };

  const handleSelect = (eventUuid: string, sessionUuid: string) => {
    setOpen(false);
    setQuery("");
    navigate(
      `/admin/events/${eventUuid}/sessions/${sessionUuid}/${trailingPath}`,
    );
  };

  const hasMore = results.length < totalCount;
  const showCurrentPin =
    debouncedQuery === "" &&
    !results.some((r) => r.sessionUuid === currentSessionUuid);

  // Trigger secondary line: drop sessionName when it duplicates eventName.
  const triggerSecondary = composeSecondary({
    sessionName: currentSessionName,
    eventName: currentEventName,
    dateLabel: currentSessionDateLabel,
  });

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label="Switch session"
          className="group inline-flex items-center gap-2 -ml-2 px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-left max-w-full"
          type="button"
        >
          <div className="flex flex-col min-w-0">
            <span className="text-base font-medium text-gray-900 dark:text-white leading-tight truncate">
              {currentEventName}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight mt-0.5 truncate">
              {triggerSecondary}
            </span>
          </div>
          <ChevronsUpDown className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[520px] p-0 overflow-hidden"
        sideOffset={6}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-slate-700">
          <Search className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          <input
            autoFocus
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sessions by event name or session label..."
            type="text"
            value={query}
          />
          {isLoading && (
            <Loader2 className="h-4 w-4 text-gray-400 dark:text-gray-500 animate-spin flex-shrink-0" />
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto py-1">
          {showCurrentPin && (
            <>
              <SectionHeader>Current</SectionHeader>
              <SessionRow
                dateLabel={currentSessionDateLabel}
                eventName={currentEventName}
                isCurrent
                onSelect={() =>
                  handleSelect(currentEventUuid, currentSessionUuid)
                }
                secondary={composeRowSecondary({
                  sessionName: currentSessionName,
                  eventName: currentEventName,
                  venueName: currentVenueName,
                })}
              />
              <Divider />
              <SectionHeader>
                {debouncedQuery === "" ? "Upcoming" : "Results"}
              </SectionHeader>
            </>
          )}
          {!showCurrentPin && results.length > 0 && (
            <SectionHeader>
              {debouncedQuery === "" ? "Upcoming" : "Results"}
            </SectionHeader>
          )}

          {results.map((r) => (
            <SessionRow
              dateLabel={formatDateLabel(r)}
              eventName={r.eventName}
              isCurrent={r.sessionUuid === currentSessionUuid}
              key={r.sessionUuid}
              onSelect={() => handleSelect(r.eventUuid, r.sessionUuid)}
              secondary={composeRowSecondary({
                sessionName: r.sessionName,
                eventName: r.eventName,
                venueName: r.venueName,
              })}
            />
          ))}

          {!isLoading && results.length === 0 && (
            <div className="px-3 py-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {debouncedQuery === ""
                  ? "No upcoming sessions."
                  : `No sessions match "${debouncedQuery}".`}
              </p>
            </div>
          )}

          {hasMore && (
            <button
              className="w-full px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              disabled={isLoadingMore}
              onClick={loadMore}
              type="button"
            >
              {isLoadingMore
                ? "Loading…"
                : `Load more (${totalCount - results.length} remaining)`}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatDateLabel(r: SessionSearchResult): string {
  if (!r.startDate) return "—";
  const d = new Date(r.startDate);
  const fmt = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return r.timezoneShortName ? `${fmt} · ${r.timezoneShortName}` : fmt;
}

/**
 * Trigger secondary line. Show sessionName only when it differs from
 * eventName; otherwise just date · tz.
 */
function composeSecondary({
  sessionName,
  eventName,
  dateLabel,
}: {
  sessionName: string | null;
  eventName: string;
  dateLabel: string;
}): string {
  const distinctName =
    sessionName && sessionName.trim() !== "" && sessionName !== eventName
      ? sessionName
      : null;
  return distinctName ? `${distinctName} · ${dateLabel}` : dateLabel;
}

/**
 * Row secondary line. Combines the venue and (only if distinct) the
 * session name.
 */
function composeRowSecondary({
  sessionName,
  eventName,
  venueName,
}: {
  sessionName: string | null;
  eventName: string;
  venueName: string | null;
}): string | null {
  const distinctName =
    sessionName && sessionName.trim() !== "" && sessionName !== eventName
      ? sessionName
      : null;
  const parts = [distinctName, venueName].filter(
    (p): p is string => !!p && p.trim() !== "",
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}
